"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useGetAllAccountsForUser } from "@/hooks/queries/accounts";
import { isUpgradeable, isOnGloryPlan, normalizeAccountType, getTrialTimeRemaining, type TrialTimeRemaining, isCCTrial, getCCTrialTimeRemaining, type CCTrialTimeRemaining, getCCTrialChargeAmount } from "@/utils/account-type";
import { FIRST_MONTH_PRICES_CENTS, YEARLY_PRICES_CENTS, type PriceTier } from "@/constants/pricing";
import LightningIcon from "@public/img/icons/lightning.svg";
import { renderIcon } from "@/utils/svg-icon";
import MorphingUpgradeText from "./MorphingUpgradeText";
import { SparklesIcon, ClockIcon } from "@heroicons/react/24/solid";
import { useUser } from "@/contexts/AuthContext";
import SkipTrialDialog from "@/components/outfame/SkipTrialDialog";

/**
 * UpgradeHeader - Linktree-style dark upgrade header bar
 *
 * Spans full width, sits above the main content area.
 * Visible on both mobile and desktop when user has upgradable accounts.
 *
 * ACCOUNT SELECTION LOGIC:
 * 1. Extract accountId from URL if on account page (/app/account/[accountId]/*)
 * 2. Fall back to localStorage stored account (sidebar selection) for non-account pages
 * 3. Show that account's info if upgradeable
 * 4. Show feature announcement if current account is on Glory (no upgrade available)
 *
 * Features:
 * - Personalized morphing messages with user avatar + @username
 * - Fixed height (40px mobile, 44px desktop) to prevent layout shifts
 * - Smooth animations matching MorphingLogo pattern
 * - Dark skeleton loaders during data fetch
 * - Responsive: works on all screen sizes
 */

// Header height constants for consistent layout offsets
export const UPGRADE_HEADER_HEIGHT = {
  mobile: 40,
  desktop: 44
};

/**
 * Formats trial time remaining as compact string
 * @example "2d 14h" or "23h 45m" or "Expired"
 */
function formatTrialTime(time: TrialTimeRemaining): string {
  if (time.isExpired) return "Expired";
  if (time.days > 0) return `${time.days}d ${time.hours}h`;
  if (time.hours > 0) return `${time.hours}h ${time.minutes}m`;
  return `${time.minutes}m`;
}
export function UpgradeHeader() {
  const $ = _c(82);
  const {
    openUpgradeModal
  } = useModalContext();
  const pathname = usePathname();
  const user = useUser();
  const {
    data: accounts,
    isLoading
  } = useGetAllAccountsForUser();
  const [skipTrialOpen, setSkipTrialOpen] = useState(false);
  let t0;
  if ($[0] !== accounts || $[1] !== pathname) {
    bb0: {
      if (!accounts || accounts.length === 0) {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = {
            currentAccount: null,
            isGlory: false,
            isTrial: false,
            isCCTrialActive: false
          };
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        t0 = t1;
        break bb0;
      }
      let t1;
      if ($[4] !== pathname) {
        t1 = pathname?.match(/\/app\/account\/([a-f0-9-]+)/i);
        $[4] = pathname;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      const urlMatch = t1;
      let targetAccountId;
      if ($[6] !== urlMatch) {
        targetAccountId = urlMatch ? urlMatch[1] : null;
        if (!targetAccountId && typeof window !== "undefined") {
          targetAccountId = localStorage.getItem("sidebar-selected-account-id");
        }
        $[6] = urlMatch;
        $[7] = targetAccountId;
      } else {
        targetAccountId = $[7];
      }
      if (targetAccountId) {
        let t2;
        if ($[8] !== targetAccountId) {
          t2 = acc => acc.id === targetAccountId;
          $[8] = targetAccountId;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        const account = accounts.find(t2);
        if (account) {
          const normalized = normalizeAccountType(account.account_type);
          t0 = {
            currentAccount: account,
            isGlory: normalized === "glory",
            isTrial: normalized === "trial",
            isCCTrialActive: isCCTrial(account)
          };
          break bb0;
        }
      }
      const firstAccount = accounts[0];
      if (!firstAccount) {
        let t2;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = {
            currentAccount: null,
            isGlory: false,
            isTrial: false,
            isCCTrialActive: false
          };
          $[10] = t2;
        } else {
          t2 = $[10];
        }
        t0 = t2;
        break bb0;
      }
      let t2;
      if ($[11] !== firstAccount.account_type) {
        t2 = normalizeAccountType(firstAccount.account_type);
        $[11] = firstAccount.account_type;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      const normalized_0 = t2;
      const t3 = normalized_0 === "glory";
      const t4 = normalized_0 === "trial";
      let t5;
      if ($[13] !== firstAccount) {
        t5 = isCCTrial(firstAccount);
        $[13] = firstAccount;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      let t6;
      if ($[15] !== firstAccount || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
        t6 = {
          currentAccount: firstAccount,
          isGlory: t3,
          isTrial: t4,
          isCCTrialActive: t5
        };
        $[15] = firstAccount;
        $[16] = t3;
        $[17] = t4;
        $[18] = t5;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      t0 = t6;
    }
    $[0] = accounts;
    $[1] = pathname;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    currentAccount,
    isGlory,
    isTrial,
    isCCTrialActive
  } = t0;
  const [trialTime, setTrialTime] = useState(null);
  let t1;
  let t2;
  if ($[20] !== currentAccount || $[21] !== isTrial) {
    t1 = () => {
      if (!isTrial || !currentAccount) {
        setTrialTime(null);
        return;
      }
      setTrialTime(getTrialTimeRemaining(currentAccount));
      const interval = setInterval(() => {
        setTrialTime(getTrialTimeRemaining(currentAccount));
      }, 60000);
      return () => clearInterval(interval);
    };
    t2 = [isTrial, currentAccount];
    $[20] = currentAccount;
    $[21] = isTrial;
    $[22] = t1;
    $[23] = t2;
  } else {
    t1 = $[22];
    t2 = $[23];
  }
  useEffect(t1, t2);
  const [ccTrialTime, setCcTrialTime] = useState(null);
  let t3;
  let t4;
  if ($[24] !== currentAccount || $[25] !== isCCTrialActive) {
    t3 = () => {
      if (!isCCTrialActive || !currentAccount) {
        setCcTrialTime(null);
        return;
      }
      setCcTrialTime(getCCTrialTimeRemaining(currentAccount));
      const interval_0 = setInterval(() => {
        setCcTrialTime(getCCTrialTimeRemaining(currentAccount));
      }, 60000);
      return () => clearInterval(interval_0);
    };
    t4 = [isCCTrialActive, currentAccount];
    $[24] = currentAccount;
    $[25] = isCCTrialActive;
    $[26] = t3;
    $[27] = t4;
  } else {
    t3 = $[26];
    t4 = $[27];
  }
  useEffect(t3, t4);
  let t5;
  if ($[28] !== currentAccount?.id || $[29] !== openUpgradeModal) {
    t5 = () => {
      openUpgradeModal(currentAccount?.id);
    };
    $[28] = currentAccount?.id;
    $[29] = openUpgradeModal;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const handleUpgradeClick = t5;
  let t6;
  if ($[31] !== currentAccount) {
    t6 = currentAccount ? normalizeAccountType(currentAccount.account_type) as PriceTier : "go";
    $[31] = currentAccount;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  const ccTrialTier = t6;
  let t7;
  if ($[33] !== currentAccount) {
    t7 = currentAccount ? getCCTrialChargeAmount(currentAccount) : FIRST_MONTH_PRICES_CENTS.go;
    $[33] = currentAccount;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const ccTrialPrice = t7;
  let t8;
  if ($[35] !== currentAccount) {
    t8 = currentAccount?.cc_trial_ends_at ? new Date(currentAccount.cc_trial_ends_at).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    }) : "";
    $[35] = currentAccount;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const ccTrialChargeDate = t8;
  if (isLoading) {
    let t9;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex items-center justify-center gap-3 bg-ink px-3 py-2 font-dashboard text-sm text-white md:gap-4 md:px-4 md:py-2.5"><div className="h-4 w-48 animate-pulse rounded bg-white/10" /><div className="h-7 w-20 animate-pulse rounded-button bg-white/10" /></div>;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    return t9;
  }
  if (isGlory && currentAccount) {
    let t9;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <SparklesIcon className="h-4 w-4 text-amber-400" />;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    let t10;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="flex items-center gap-2">{t9}<span className="text-white/80"><span className="hidden sm:inline">You're on Glory — </span><span className="font-medium text-white">Try AI Max Beta</span><span className="hidden md:inline"> for blazing-fast growth</span></span></div>;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    const t11 = `/app/account/${currentAccount.id}/max`;
    let t12;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SparklesIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    let t13;
    if ($[41] !== t11) {
      t13 = <div className="flex items-center justify-center gap-3 bg-ink px-3 py-2 font-dashboard text-sm text-white md:gap-4 md:px-4 md:py-2.5">{t10}<Link href={t11} className="flex shrink-0 items-center gap-1.5 rounded-button bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-1.5 text-xs font-semibold text-black transition-all duration-200 hover:brightness-110 active:scale-[0.98] md:px-4 md:text-sm">{t12}Try Max</Link></div>;
      $[41] = t11;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    return t13;
  }
  if (!currentAccount) {
    return null;
  }
  if (isCCTrialActive && ccTrialTime) {
    let t10;
    let t9;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        fontVariantNumeric: "tabular-nums"
      };
      t10 = <ClockIcon className="h-3.5 w-3.5 text-emerald-400 md:h-4 md:w-4" />;
      $[43] = t10;
      $[44] = t9;
    } else {
      t10 = $[43];
      t9 = $[44];
    }
    const t11 = ccTrialTime.days > 0 ? `${ccTrialTime.days}d ${ccTrialTime.hours}h` : `${ccTrialTime.hours}h ${ccTrialTime.minutes}m`;
    let t12;
    if ($[45] !== t11) {
      t12 = <span>Trial: <span className="font-semibold text-white">{t11}</span> left</span>;
      $[45] = t11;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    let t13;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <span className="text-white/30">·</span>;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    let t14;
    if ($[48] !== ccTrialPrice) {
      t14 = Math.round(ccTrialPrice / 100);
      $[48] = ccTrialPrice;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    let t15;
    if ($[50] !== ccTrialChargeDate || $[51] !== t14) {
      t15 = <span>${t14} on {ccTrialChargeDate}</span>;
      $[50] = ccTrialChargeDate;
      $[51] = t14;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    let t16;
    if ($[53] !== t12 || $[54] !== t15) {
      t16 = <div className="flex items-center gap-1.5 text-xs text-white/70 md:text-sm" style={t9}>{t10}{t12}{t13}{t15}</div>;
      $[53] = t12;
      $[54] = t15;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    let t17;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => setSkipTrialOpen(true);
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    let t18;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <button onClick={t17} className="group flex shrink-0 items-center gap-1 rounded-button bg-pink px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-pink-dark active:scale-[0.98] md:gap-1.5 md:px-4 md:text-sm">Start plan now<span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span></button>;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    let t19;
    if ($[58] !== t16) {
      t19 = <div className="flex items-center justify-center gap-3 bg-ink px-3 py-2 font-dashboard text-sm text-white md:gap-4 md:px-4 md:py-2.5">{t16}{t18}</div>;
      $[58] = t16;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    let t20;
    if ($[60] !== ccTrialPrice || $[61] !== ccTrialTier || $[62] !== currentAccount || $[63] !== skipTrialOpen || $[64] !== user) {
      t20 = user?.id && currentAccount.cc_trial_ends_at && <SkipTrialDialog open={skipTrialOpen} onClose={() => setSkipTrialOpen(false)} accountId={currentAccount.id} planTier={ccTrialTier as "go" | "grow" | "glory"} firstMonthPrice={ccTrialPrice} ccTrialEndsAt={currentAccount.cc_trial_ends_at} userId={user.id} isYearly={currentAccount.billing_period === "yearly"} onSuccess={() => {
        setSkipTrialOpen(false);
        window.location.reload();
      }} />;
      $[60] = ccTrialPrice;
      $[61] = ccTrialTier;
      $[62] = currentAccount;
      $[63] = skipTrialOpen;
      $[64] = user;
      $[65] = t20;
    } else {
      t20 = $[65];
    }
    let t21;
    if ($[66] !== t19 || $[67] !== t20) {
      t21 = <>{t19}{t20}</>;
      $[66] = t19;
      $[67] = t20;
      $[68] = t21;
    } else {
      t21 = $[68];
    }
    return t21;
  }
  let t9;
  if ($[69] !== currentAccount?.avatar_url || $[70] !== currentAccount?.username || $[71] !== handleUpgradeClick || $[72] !== isLoading || $[73] !== isTrial || $[74] !== trialTime) {
    t9 = isTrial && trialTime ? <button onClick={handleUpgradeClick} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 hover:scale-[1.02] md:text-sm ${trialTime.isExpired ? "bg-red-500/20 text-red-300 hover:bg-red-500/30" : "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30"}`}><ClockIcon className="h-3.5 w-3.5 md:h-4 md:w-4" /><span>Trial: <span className="font-semibold">{formatTrialTime(trialTime)}</span>{!trialTime.isExpired && <span className="hidden sm:inline"> left</span>}</span></button> : <MorphingUpgradeText avatarUrl={currentAccount?.avatar_url} username={currentAccount?.username} isLoading={isLoading} morphInterval={5000} />;
    $[69] = currentAccount?.avatar_url;
    $[70] = currentAccount?.username;
    $[71] = handleUpgradeClick;
    $[72] = isLoading;
    $[73] = isTrial;
    $[74] = trialTime;
    $[75] = t9;
  } else {
    t9 = $[75];
  }
  let t10;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = renderIcon(LightningIcon, "w-3.5 h-3.5 md:w-4 md:h-4");
    $[76] = t10;
  } else {
    t10 = $[76];
  }
  let t11;
  if ($[77] !== handleUpgradeClick) {
    t11 = <button onClick={handleUpgradeClick} className="flex shrink-0 items-center gap-1 rounded-button bg-pink px-3 py-1.5 text-xs font-semibold text-black transition-all duration-200 hover:bg-pink-dark active:scale-[0.98] active:opacity-80 md:gap-1.5 md:px-4 md:text-sm">{t10}Upgrade</button>;
    $[77] = handleUpgradeClick;
    $[78] = t11;
  } else {
    t11 = $[78];
  }
  let t12;
  if ($[79] !== t11 || $[80] !== t9) {
    t12 = <div className="flex items-center justify-center gap-3 bg-ink px-3 py-2 font-dashboard text-sm text-white md:gap-4 md:px-4 md:py-2.5">{t9}{t11}</div>;
    $[79] = t11;
    $[80] = t9;
    $[81] = t12;
  } else {
    t12 = $[81];
  }
  return t12;
}